import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Copy } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { okaidia } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { IWebhookAttempt } from '@/types';
import Drawer from '../Modals/Drawer';
import Time from '../Time';
import { Badge } from '../ui/badge';

interface DrawerProps {
  webhookAttempt: IWebhookAttempt;
}

const WebhookDrawer = NiceModal.create(({ webhookAttempt }: DrawerProps) => {
  const modal = useModal('webhook-drawer');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        alert('Copied to clipboard!');
      },
      (err) => {
        alert('Failed to copy!');
        console.error('Clipboard copy failed:', err);
      },
    );
  };

  const parseJSON = (str: string = '') => {
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch (error) {
      console.error('Invalid JSON:', error);
      return str;
    }
  };

  return (
    <Drawer modal={modal} title={`Webhook Attempt`}>
      <div className="p-4 space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="font-bold text-gray-600">ID:</p>
            <p className="text-gray-800">{webhookAttempt.id}</p>
          </div>

          <div>
            <p className="font-bold text-gray-600">Status:</p>
            <Badge className="capitalize">{webhookAttempt.status}</Badge>
          </div>
          <div>
            <p className="font-bold text-gray-600">Response Code:</p>
            {Number(webhookAttempt.response_code) > 200 && Number(webhookAttempt.response_code) < 300 ? (
              <Badge className="bg-green-600">{webhookAttempt.response_code}</Badge>
            ) : (
              <Badge variant={'destructive'}>{webhookAttempt.response_code}</Badge>
            )}
          </div>
          <div>
            <p className="font-bold text-gray-600">Attempted At:</p>
            <Time>{webhookAttempt.attempted_at}</Time>
          </div>
          <div>
            <p className="font-bold text-gray-600">Created At:</p>
            <Time>{webhookAttempt.created_at}</Time>
          </div>
          <div>
            <p className="font-bold text-gray-600">Updated At:</p>
            <Time>{webhookAttempt.updated_at}</Time>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold">Webhook URL</h2>
            <button
              onClick={() => copyToClipboard(webhookAttempt.payload)}
              className="text-gray-500 hover:text-gray-700"
              title="Copy Payload"
            >
              <Copy size={20} />
            </button>
          </div>
          <SyntaxHighlighter
            language="text"
            style={okaidia}
            customStyle={{
              borderRadius: '8px',
              padding: '16px',
              fontSize: '14px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {webhookAttempt.webhook.destination}
          </SyntaxHighlighter>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold">Payload</h2>
            <button
              onClick={() => copyToClipboard(webhookAttempt.webhook.payload)}
              className="text-gray-500 hover:text-gray-700"
              title="Copy Payload"
            >
              <Copy size={20} />
            </button>
          </div>
          <SyntaxHighlighter
            language="json"
            style={okaidia}
            customStyle={{
              borderRadius: '8px',
              padding: '16px',
              fontSize: '14px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {JSON.stringify(webhookAttempt.webhook.payload, null, 2)}
          </SyntaxHighlighter>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold">Response</h2>
            <button
              onClick={() => copyToClipboard(webhookAttempt.response_body)}
              className="text-gray-500 hover:text-gray-700"
              title="Copy Response"
            >
              <Copy size={20} />
            </button>
          </div>
          <SyntaxHighlighter
            language="json"
            style={okaidia}
            customStyle={{
              borderRadius: '8px',
              padding: '16px',
              fontSize: '14px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {parseJSON(webhookAttempt.response_body)}
          </SyntaxHighlighter>
        </div>
      </div>
    </Drawer>
  );
});

export default WebhookDrawer;
